import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-input-mask';
import Icon from 'Components/Icon';
import Modal from 'Components/Modal';
import EnvironmentManager from 'Utils/environmentManager';
import { thousandFormatter, createAmountChangeHandler, createDecimalChangeHandler } from 'Utils/decimalFormatter';

const cms = key => EnvironmentManager.getMainMessageText({ key });

const formatInitialAmount = value => (!value || value === '0') ? '' : value;
const formatInitialDecimal = value => (!value || value === '00') ? '' : value;

const ActionSheet = ({
  isOpen,
  onClose,
  onConfirmRequest = () => {},
  initialIban = '',
  initialAmount = '',
  initialAmountDecimal = '',
  isSubmitting = false,
  isRepeat = false
}) => {
  const [iban, setIban] = useState(initialIban);
  const [amount, setAmount] = useState(formatInitialAmount(initialAmount));
  const [amountDecimal, setAmountDecimal] = useState(formatInitialDecimal(initialAmountDecimal));
  const [amountFocused, setAmountFocused] = useState(false);
  const [decimalFocused, setDecimalFocused] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIban(initialIban || '');
      setAmount(formatInitialAmount(initialAmount));
      setAmountDecimal(formatInitialDecimal(initialAmountDecimal));
    }
  }, [isOpen, initialIban, initialAmount, initialAmountDecimal]);

  const handleIbanChange = e => {
    const value = e.target.value.replace(/ /g, '');
    setIban(value);
  };

  const handleClearIban = () => {
    setIban('');
  };

  const handleAmountChange = createAmountChangeHandler(setAmount, 10);
  const handleAmountDecimalChange = createDecimalChangeHandler(setAmountDecimal);

  const handleClose = () => {
    setIban('');
    setAmount('');
    setAmountDecimal('');
    onClose();
  };

  const ibanWithoutSpaces = iban.replace(/ /g, '');
  const ibanValid = ibanWithoutSpaces.length === 26;
  const totalAmount = parseFloat(`${amount || '0'}.${amountDecimal || '00'}`);
  const isSubmitDisabled = !ibanValid || totalAmount <= 0 || isSubmitting;

  const handleSubmitClick = () => {
    onConfirmRequest({
      iban: ibanWithoutSpaces,
      amount,
      amountDecimal
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={ true }
      onClose={ handleClose }
      position={ { web: 'center' } }
      className="fast-deposit-modal"
    >
      <div className="fast-deposit-action-sheet">
        <div className="fast-deposit-action-sheet__header">
          <h2 className="fast-deposit-action-sheet__title">{ cms(isRepeat ? 'fast_deposit_page_repeat_payment_request_title' : 'fast_deposit_page_send_payment_request_title') }</h2>
          <button
            type="button"
            className="fast-deposit-action-sheet__close"
            onClick={ handleClose }
          >
            <Icon iconName="svgFastDepositClose" />
          </button>
        </div>

        <p className="fast-deposit-action-sheet__description">
          { cms('fast_deposit_page_send_payment_request_description') }
        </p>

        <div className="fast-deposit-action-sheet__info-box">
          <Icon iconName="svgFastDepositInfo" className="fast-deposit-action-sheet__info-box__icon" />
          <span className="fast-deposit-action-sheet__info-box__text"
            dangerouslySetInnerHTML={ { __html: cms('fast_deposit_page_info_text') } }
          />
        </div>

        <div className="fast-deposit-action-sheet__form">
          <div className="fast-deposit-action-sheet__field">
            <label className="fast-deposit-action-sheet__field__label">{ cms('fast_deposit_page_iban_number_label') }</label>
            <div className={ `fast-deposit-action-sheet__field__input-wrapper ${ibanWithoutSpaces.length > 2 ? 'fast-deposit-action-sheet__field__input-wrapper--filled' : ''}` }>
              <MaskedInput
                type="text"
                inputMode="numeric"
                className="fast-deposit-action-sheet__field__input fast-deposit-action-sheet__field__input--iban"
                mask="TR99 9999 9999 9999 9999 9999 99"
                maskChar={ null }
                value={ iban }
                onChange={ handleIbanChange }
                placeholder="TR"
              />
              { ibanWithoutSpaces.length > 2 && (
                <button
                  type="button"
                  className="fast-deposit-action-sheet__field__clear"
                  onClick={ handleClearIban }
                >
                  <Icon iconName="svgFastDepositIbanClose" className="fast-deposit-action-sheet__field__clear__icon" />
                </button>
              ) }
            </div>
          </div>

          <div className="fast-deposit-action-sheet__field">
            <label className="fast-deposit-action-sheet__field__label">{ cms('fast_deposit_page_amount_send_label') }</label>
            <div className="fast-deposit-action-sheet__field__amount-wrapper">
              <div className={ `fast-deposit-action-sheet__field__input-wrapper fast-deposit-action-sheet__field__input-wrapper--amount ${amountFocused || amount ? 'fast-deposit-action-sheet__field__input-wrapper--filled' : ''}` }>
                <input
                  type="text"
                  inputMode="numeric"
                  className="fast-deposit-action-sheet__field__input fast-deposit-action-sheet__field__input--amount-main"
                  value={ amount ? thousandFormatter(amount) : '' }
                  onChange={ handleAmountChange }
                  onFocus={ () => setAmountFocused(true) }
                  onBlur={ () => setAmountFocused(false) }
                  placeholder="0"
                />
              </div>
              <span className="fast-deposit-action-sheet__field__amount-separator">,</span>
              <div className={ `fast-deposit-action-sheet__field__input-wrapper fast-deposit-action-sheet__field__input-wrapper--decimal ${decimalFocused || amountDecimal ? 'fast-deposit-action-sheet__field__input-wrapper--filled' : ''}` }>
                <input
                  type="text"
                  inputMode="numeric"
                  className="fast-deposit-action-sheet__field__input fast-deposit-action-sheet__field__input--amount-decimal"
                  value={ amountDecimal }
                  onChange={ handleAmountDecimalChange }
                  onFocus={ () => setDecimalFocused(true) }
                  onBlur={ () => setDecimalFocused(false) }
                  maxLength={ 2 }
                  placeholder="00"
                />
              </div>
              <span className="fast-deposit-action-sheet__field__amount-currency">TL</span>
            </div>
          </div>

          <button
            type="button"
            className={ `fast-deposit-action-sheet__submit ${isSubmitDisabled ? 'fast-deposit-action-sheet__submit--disabled' : ''}` }
            onClick={ handleSubmitClick }
            disabled={ isSubmitDisabled }
          >
            { cms('fast_deposit_page_send_Bilyoner_button') }
          </button>
        </div>
      </div>
    </Modal>
  );
};

ActionSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmRequest: PropTypes.func,
  initialIban: PropTypes.string,
  initialAmount: PropTypes.string,
  initialAmountDecimal: PropTypes.string,
  isSubmitting: PropTypes.bool,
  isRepeat: PropTypes.bool
};

export default ActionSheet;
